import { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Grid,
  Typography,
  Box,
  Button,
  TextField,
  DialogActions,
  FormControlLabel,
  Checkbox,
  Tooltip,
  ClickAwayListener
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import Autocomplete, { createFilterOptions } from '@mui/material/Autocomplete';
import { budgetForm, createGlCode, getBudget, deleteGlCodes } from '../../../../redux/slices/budget';
import { useDispatch, useSelector } from '../../../../redux/store';
import Iconify from '../../../../reusable-components/iconify';
import { useSnackbar } from '../../../../reusable-components/snackbar';
import { formatMoney } from '../../../../utils/formatNumber';
import { AwaitButton } from '../../../../reusable-components/await-button';
import CloseIconButton from '../../../../reusable-components/basic-buttons/CloseIconButton';

BudgetModal.propTypes = {
  edit: PropTypes.bool,
  row: PropTypes.object,
  selectedRow: PropTypes.object,
};
const filter = createFilterOptions();

export default function BudgetModal({ edit, row }) {
  const {
    data: { agoraCategories, agoraSubcategories, facilities, glCodes, budgets },
  } = useSelector((state) => state.budget);

  const dispatch = useDispatch();
  const [open, setOpen] = useState(false);
  const [autocompleteOpen, setAutocompleteOpen] = useState(false);
  const [glCodesToDelete, setGlCodesToDelete] = useState([]);
  const [isDeleteGLCodeModeOn, setIsDeleteGLCodeModeOn] = useState(false);
  const [glCodesAndAssociatedBudgets, setGlCodesAndAssociatedBudgets] = useState([]);
  const [isConfirmDeleteDialogOpen, setIsConfirmDeleteDialogOpen] = useState(false);
  const [glCodesToDisplay, setGlCodesToDisplay] = useState(glCodes);
  const { enqueueSnackbar } = useSnackbar();

  const [value, setValue] = useState({
    facility: '',
    agoraCategory: '',
    agoraSubcategory: '',
    glCode: '',
    ppdBudget: '',
    flatRate:false,
    excludeFromMain: false,
  });

  const glCodesToDeleteTooltipContent = glCodesToDelete.length ? (
    <Box style={{maxWidth: 300}}>
      <Box>Delete GL Codes:</Box>
      <Box>
        {glCodesToDelete.map(code => code.label).join(", ")}
      </Box>
    </Box>
  ) : (
    <Box style={{maxWidth: 300}}>
      <Box>
        No GL Codes Selected
      </Box>
    </Box>
  );

  useEffect(() => {
    if(!isDeleteGLCodeModeOn) {
      setGlCodesToDelete([]);
    }
  }, [isDeleteGLCodeModeOn])

  useEffect(() => {
    setGlCodesToDisplay(glCodes);
  }, [glCodes])

  const checkIfGlCodesAssociatedWithBudgets = async() => {
    const glCodesAssociatedWithBudget = [];
    glCodesToDelete.forEach(glCode => {
      if(budgets.some(budget => budget.glId === glCode.id)) {
        const budgetsWithGlCode = budgets.filter(budget => budget.glId === glCode.id);
        glCodesAssociatedWithBudget.push({'glCodeName': glCode.label, 'amountBudgets': budgetsWithGlCode.length});
      }
    })
    setGlCodesAndAssociatedBudgets(glCodesAssociatedWithBudget);
    if(glCodesAssociatedWithBudget.length) {
      setIsConfirmDeleteDialogOpen(true);
    }
    else deleteSelectedGlCodes();
  }

  const handleConfirmDeleteDialogClose = () => {
    setIsConfirmDeleteDialogOpen(false);
  }

  const deleteSelectedGlCodes = () => {
    const glIdsToDelete = glCodesToDelete.map(glCode => glCode.id);
    dispatch(deleteGlCodes(glIdsToDelete))
      .then(() => {
        setGlCodesToDisplay(glCodesToDisplay.filter(glCode => !glIdsToDelete.includes(glCode.id)));
        setIsDeleteGLCodeModeOn(false);
        enqueueSnackbar('GL codes deleted successfully', { variant: 'success' })
      })
      .catch((error) => {
        console.error(error);
        enqueueSnackbar('Error deleting GL codes', { variant: 'error' })
      });
  }
  
  const handleSave = async () => {
    const response = await dispatch(budgetForm(value, edit ? row?.id : null));
    if (response) enqueueSnackbar('Budget updated successfully', { variant: 'success' });
    else enqueueSnackbar('Error updating budget', { variant: 'error' });
    dispatch(getBudget());
    setOpen(false);
  };
  const handleCreateGlCode = async (glCode) => {
    if (!value?.facility?.id) {
      enqueueSnackbar('You must select a facility', { variant: 'error' });
      setValue({ ...value, glCode: '' });
    }
    else if (!glCode || !glCode.trim()) enqueueSnackbar('GL code requires at least one character', { variant: 'error' });
    else if (glCode.length > 100) enqueueSnackbar('GL code must be 100 characters or less', { variant: 'error' });
    else {
      const response = await dispatch(createGlCode(glCode, value.facility.facilityBookingTypeId));
      if (response) {
        setValue({
          ...value,
          glCode: {
            id: response,
            label: glCode,
            facilityBookingTypeId: value.facility.facilityBookingTypeId,
          },
        });
        enqueueSnackbar('GL code added successfully', { variant: 'success' });
      } else {
        enqueueSnackbar('Failed to add GL code', { variant: 'error' });
      }
    }
  };

  const handleClickOpen = () => {
    const fullGL = edit && row.glId ? glCodes.find(code => code.id === row.glId) : null;
    setValue({
      facility: edit
        ? {
          id: row.facilityId,
          label: row.facility,
          facilityBookingTypeId: facilities.find((facility) => facility.id === row.facilityId)?.facilityBookingTypeId,
        }
        : '',
      agoraCategory: edit ? { id: row.agoraCategoryId, label: row.agoraCategory } : '',
      agoraSubcategory: edit ? { id: row.agoraSubcategoryId, label: row.agoraSubcategory } : '',
      glCode: edit && row.glCode ? fullGL ? {
        id: fullGL.id,
        label: !fullGL.description ? fullGL.code : `${fullGL.code} (${fullGL.description})`,
        facilityBookingTypeId: fullGL.facilityBookingTypeId
      } : {
        id: row.glId,
        label: row.glCode,
        facilityBookingTypeId: facilities.find((facility) => facility.id === row.facilityId)
          ?.facilityBookingTypeId,
      } : '',
      ppdBudget: edit ? row.ppdBudget : '',
      flatRate: edit ? row.flatRate : false,
      excludeFromMain: edit ? row.excludeFromMain : false,
    });
    setOpen(true);
  };
  const handleClose = () => {
    setValue({
      facility: '',
      agoraCategory: '',
      agoraSubcategory: '',
      glCode: '',
      ppdBudget: '',
      flatRate:false,
      excludeFromMain: false,
    });
    setOpen(false);
    setIsDeleteGLCodeModeOn(false);
  };

  return (
    <>
      {edit ? (
        <IconButton edge="end" color="secondary" onClick={handleClickOpen} sx={{ mr: 0.5 }}>
          <EditIcon />
        </IconButton>
      ) : (
        <Button
          variant="contained"
          sx={{ mr: 1 }}
          size="small"
          color="secondary"
          onClick={handleClickOpen}
          startIcon={<Iconify icon="eva:plus-outline" />}
        >
          Add New Budget
        </Button>
      )}

      <Dialog open={open} onClose={(event, reason) => {
        if (reason === 'backdropClick' || reason === 'escapeKeyDown') return;
        handleClose();
      }} maxWidth="sm" fullWidth size="small">
        <DialogTitle
          sx={{
            textAlign: 'center',
          }}
        >
          {edit ? 'Edit' : 'Add'} Budget
          <CloseIconButton onClick={handleClose}/>
        </DialogTitle>
        <DialogContent sx={{ height: '100%', width: '100%' }}>
          <Grid container direction="row" justifyContent="center" alignItems="flex-start" sx={{ p: 1 }}>
            <Grid
              container
              direction="row"
              justifyContent="center"
              alignItems="flex-start"
              sx={{ mt: 1.5 }}
              spacing={2}
            >
              <Box sx={{ width: '100%' }}>
                <Typography variant="subtitle1" sx={{ mb: 1 }}>
                  Facility
                </Typography>
                <Autocomplete
                  fullWidth
                  size="small"
                  value={value.facility}
                  onChange={(event, newValue) => {
                    setValue({
                      ...value,
                      glCode: '',
                      facility: newValue,
                    });
                  }}
                  options={facilities?.map((type) => ({
                    id: type.id,
                    label: type.facilityName,
                    facilityBookingTypeId: type.facilityBookingTypeId,
                  }))}
                  renderInput={(params) => <TextField {...params} label="Search or select" autoComplete="off"/>}
                  sx={{
                    '& legend': { display: 'none' },
                    '& fieldset': { top: 0 },
                    '& .MuiFormLabel-root ': { display: 'none' },
                  }}
                />
              </Box>
            </Grid>
            <Grid
              container
              direction="row"
              justifyContent="center"
              alignItems="flex-start"
              sx={{ mt: 1.5 }}
              spacing={2}
            >
              <Box sx={{ width: '100%' }}>
                <Typography variant="subtitle1" sx={{ mb: 1 }}>
                  PS Category
                </Typography>
                <Autocomplete
                  fullWidth
                  size="small"
                  value={value.agoraCategory}
                  onChange={(event, newValue) => {
                    setValue({
                      ...value,
                      agoraSubcategory: '',
                      agoraCategory: newValue,
                    });
                  }}
                  options={agoraCategories?.map((type) => ({ id: type.id, label: type.name }))}
                  renderInput={(params) => <TextField {...params} label="Search or select" autoComplete="off"/>}
                  sx={{
                    '& legend': { display: 'none' },
                    '& fieldset': { top: 0 },
                    '& .MuiFormLabel-root ': { display: 'none' },
                  }}
                />
              </Box>
            </Grid>
            <Grid
              container
              direction="row"
              justifyContent="center"
              alignItems="flex-start"
              sx={{ mt: 1.5 }}
              spacing={2}
            >
              <Box sx={{ width: '100%' }}>
                <Typography variant="subtitle1" sx={{ mb: 1 }}>
                  PS Subcategory
                </Typography>
                <Autocomplete
                  fullWidth
                  size="small"
                  value={value.agoraSubcategory}
                  onChange={(event, newValue) => {
                    setValue({
                      ...value,
                      agoraSubcategory: newValue,
                    });
                  }}
                  getOptionDisabled={(option) => !!budgets.find(budget => budget.facilityId === value.facility.id && budget.agoraSubcategoryId === option.id)}
                  options={agoraSubcategories
                    .filter(
                      (type) => type?.mainCategoryId === value?.agoraCategory?.id
                    )?.map((type) => ({ id: type.id, label: type.name }))}
                  renderInput={(params) => <TextField {...params} label="Search or select" autoComplete="off"/>}
                  sx={{
                    '& legend': { display: 'none' },
                    '& fieldset': { top: 0 },
                    '& .MuiFormLabel-root ': { display: 'none' },
                  }}
                />
              </Box>
            </Grid>
            <Grid
              container
              direction="row"
              justifyContent="center"
              alignItems="flex-start"
              sx={{ mt: 1.5 }}
              spacing={2}
            >
              <Box sx={{ width: '100%' }}>
                <Box style={{display: 'flex', alignItems: 'end'}}>
                  <Typography variant="subtitle1" sx={{ mb: 1 }}>
                      GL Code
                  </Typography>
                  {
                    value.facility && value.agoraCategory && value.agoraSubcategory && !isDeleteGLCodeModeOn ? 
                    <Button
                      sx={{ml: 'auto', mr: 1}}
                      variant="text"
                      color="error"
                      onClick={(e) => {
                        e.stopPropagation();
                        setIsDeleteGLCodeModeOn(true);
                        setAutocompleteOpen(true);
                      }}
                    > Delete GL Codes
                    </Button>
                    : value.facility && value.agoraCategory && value.agoraSubcategory ?
                    <Box style={{marginLeft: 'auto', marginBottom: 1}}>
                      <Tooltip
                        title={glCodesToDeleteTooltipContent}
                        placement='top'
                      >
                          <Button
                            variant="text"
                            color="error"
                            disabled={!glCodesToDelete.length}
                            onClick={checkIfGlCodesAssociatedWithBudgets}
                          >
                            <DeleteIcon/>
                            Delete {glCodesToDelete.length ? `${glCodesToDelete.length} selected GL Codes` : `GL Codes`}
                          </Button>
                      </Tooltip> 
                      <Button onClick={() => setIsDeleteGLCodeModeOn(false)}>Cancel</Button>
                   </Box> : <></>
                  }
                </Box>
                <ClickAwayListener onClickAway={() => setAutocompleteOpen(false)}>
                  <Autocomplete
                    open={autocompleteOpen}
                    onOpen={() => setAutocompleteOpen(true)}
                    onClose={() => setAutocompleteOpen(false)}
                    fullWidth
                    size="small"
                    freeSolo
                    selectOnFocus
                    handleHomeEndKeys
                    value={value?.glCode || ''}
                    onChange={(event, newValue) => {
                      if (newValue?.inputValue) {
                        handleCreateGlCode(newValue.inputValue);
                      } else {
                        setValue({
                          ...value,
                          glCode: newValue || '',
                        });
                      }
                    }}
                    options={glCodesToDisplay
                      .filter((type) => type?.facilityBookingTypeId === value?.facility?.facilityBookingTypeId)
                      .map((type) => ({ id: type.id, label: !type.description ? type.code : `${type.code} (${type.description})` }))}
                    isOptionEqualToValue={(option, value) => option?.id === value?.id}
                    filterOptions={(options, params) => {
                      const filtered = filter(options, params);

                      const { inputValue } = params;
                      // Suggest the creation of a new value
                      const isExisting = options.some((option) => inputValue === option.label);
                      if (inputValue !== '' && !isExisting) {
                        filtered.push({
                          inputValue,
                          label: `Add "${inputValue}"`,
                        });
                      }

                      return filtered;
                    }}
                    getOptionLabel={(option) => {
                      if (typeof option === 'string') {
                        return option;
                      }
                      if (option.inputValue) {
                        return option.inputValue;
                      }
                      return option.label;
                    }}
                    renderOption={(props, option) => 
                      <li {...props} key={option.id}>
                        {
                          isDeleteGLCodeModeOn && 
                          <Checkbox
                            checked={!!glCodesToDelete.find(code => code.id === option.id)}
                            onClick={(event) => event.stopPropagation()}
                            onChange={() => {
                              setGlCodesToDelete(
                                glCodesToDelete.find(code => code.id === option.id) ?
                                glCodesToDelete.filter(code => code.id !== option.id) :
                                [...glCodesToDelete, option]
                              );
                            }}
                          />

                        }
                        {option.label}
                      </li>
                    }
                    renderInput={(params) => <TextField {...params} label="Select or add your own" autoComplete="off"/>}
                  />
                </ClickAwayListener>
              </Box>
            </Grid>
            <Grid
              container
              direction="row"
              justifyContent="center"
              alignItems="flex-start"
              sx={{ mt: 1.5 }}
              spacing={2}
            >
              <Box sx={{ width: '100%' }}>
                <Typography variant="subtitle1" sx={{ mb: 1 }}>
                  Budget
                </Typography>
                <TextField
                  value={value.ppdBudget}
                  type="number"
                  onChange={(e) =>
                    setValue({
                      ...value,
                      ppdBudget: formatMoney(e.target.value),
                    })
                  }
                  label=""
                  variant="outlined"
                  fullWidth
                  size="small"
                  autoComplete="off"
                />
              </Box>
            </Grid>
            <Grid
              container
              direction="row"
              justifyContent="center"
              alignItems="flex-start"
              sx={{ mt: 1.5 }}
              spacing={2}
            >
              <Box sx={{ width: '100%' }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={value.excludeFromMain}
                      onChange={(event) => {
                        setValue({
                          ...value,
                          excludeFromMain: event.target.checked,
                        });
                      }}
                    />
                  }
                  label="Exclude from main budget"
                />
              </Box>
            </Grid>
            <Grid
              container
              direction="row"
              justifyContent="center"
              alignItems="flex-start"
              sx={{ mt: 1.5 }}
              spacing={2}
            >
              <Box sx={{ width: '100%' }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={value.flatRate}
                      onChange={(event) => {
                        setValue({
                          ...value,
                          flatRate: event.target.checked,
                        });
                      }}
                    />
                  }
                  label="Flat Rate"
                />
              </Box>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" color="error" size="medium" sx={{ width: 150, mr: 2 }} onClick={handleClose}>
            Cancel
          </Button>
          <AwaitButton
            variant="contained"
            color="primary"
            size="medium"
            disabled={value.facility === '' || value.agoraCategory === '' || value.agoraSubcategory === ''}
            sx={{ width: 150 }}
            onClick={handleSave}
          >
            {edit ? 'Save' : 'Add Budget'}
          </AwaitButton>
        </DialogActions>
      </Dialog>
      <Dialog
        fullWidth maxWidth="xs"
        open={isConfirmDeleteDialogOpen}
        onClose={(event, reason) => {
          if (reason === 'backdropClick' || reason === 'escapeKeyDown') return;
          handleConfirmDeleteDialogClose();
        }}
      >
          <DialogTitle sx={{ pb: 2 }}>Delete</DialogTitle>
          <DialogContent sx={{ typography: 'body2' }}> 
            <Box>Are you sure you want to delete the selected GL codes?</Box>
            {
              glCodesAndAssociatedBudgets.map((glCodeAndBudgets) =>
                <Box key={glCodeAndBudgets.glCodeName}>
                  GL code {glCodeAndBudgets.glCodeName} is associated with {glCodeAndBudgets.amountBudgets} budgets.
                </Box>
              )
            }
          </DialogContent>
          <DialogActions>
            <AwaitButton variant="contained" color='error' onClick={deleteSelectedGlCodes}>
              Delete
            </AwaitButton>

            <Button variant="outlined" color="inherit" onClick={handleConfirmDeleteDialogClose}>
              Cancel
            </Button>
          </DialogActions>
        </Dialog>
    </>
  );
}
